import { RouteLocationNormalized } from 'vue-router';

import { isPublicRoute } from '@/routerAccess';
import { isRedirectCallback } from '@/services/auth_microsoft';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/user';

async function ensureTenantSelected() {
    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    await authStore.waitForUserData(2000);

    if (!tenantStore.tenantId && userStore.tenants?.length) {
        tenantStore.updateTenant(userStore.tenants[0]);
    }
}

function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export default async function authGuard(to: RouteLocationNormalized) {
    const authStore = useAuthStore();
    const accessContextStore = useAccessContextStore();

    try {
        /* ------------------------------------------------------------
         * Handle Microsoft redirect hash
         * ------------------------------------------------------------ */
        if (to.path === '/' && to.hash) {
            authStore.microsoftRedirectHash = to.hash;
            return '/';
        }

        /* ------------------------------------------------------------
         * Handle Microsoft redirect callback
         * ------------------------------------------------------------ */
        if (to.path === '/' && isRedirectCallback()) {
            await sleep(1000);
        }

        await authStore.waitForAuthReady(3000);

        /* ------------------------------------------------------------
         * Handle public routes
         * ------------------------------------------------------------ */
        if (!authStore.isDescopeEnabled && isPublicRoute(to)) {
            return '/';
        }

        if (isPublicRoute(to)) {
            if (authStore.isAuthenticated) {
                return '/';
            }
            return true;
        }

        /* ------------------------------------------------------------
         * Handle authenticated routes
         * ------------------------------------------------------------ */
        if (!authStore.isAuthenticated) {
            accessContextStore.clear();

            if (authStore.isDescopeEnabled) {
                return '/login';
            }

            if (to.path !== '/') {
                return { path: '/', query: to.query };
            }

            await authStore.waitForAuthInitialized(5000);

            if (authStore.isAuthenticated) {
                return { path: '/', query: to.query };
            }

            return true;
        }

        await ensureTenantSelected();
        return true;
    } catch (error) {
        console.error('Error in auth router guard:', error);
        return authStore.isDescopeEnabled
            ? { path: '/login', query: { error: 'Authentication failed' } }
            : '/';
    }
}
